import { useState, useEffect, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import SignatureCanvas from "react-signature-canvas";
import { findSignaturePlaceholders, stampSignature } from "pdf-sign-pad";
import { compressPdfIfNeeded } from "../utils/compressPdf";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";

// Crop a canvas to its non-transparent bounding box and return a PNG data URL.
// (Replaces react-signature-canvas's getTrimmedCanvas, whose bundled trim-canvas
// dep throws "trim_canvas ... is not a function" under webpack.)
const MAX_SIG_WIDTH = 240;
const trimmedPng = (canvas) => {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width,
    minY = height,
    maxX = 0,
    maxY = 0,
    found = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return canvas.toDataURL("image/png");
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const scale = Math.min(1, MAX_SIG_WIDTH / w);
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));
  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  out.getContext("2d").drawImage(canvas, minX, minY, w, h, 0, 0, outW, outH);
  return out.toDataURL("image/png");
};

// Fountain-pen / edit icon (Figma Bedek-v.2 77:3550).
const PenIcon = ({ color = "#fff", size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 19.5668 19.5668"
    fill="none"
    style={{ display: "block", flexShrink: 0 }}
  >
    <path
      d="M7.64286 17.6382C8.03764 17.2743 8.35273 16.8326 8.56825 16.3408C8.78377 15.8491 8.89504 15.318 8.89504 14.7811C8.89504 14.2442 8.78377 13.7131 8.56825 13.2213C8.35273 12.7296 8.03764 12.2878 7.64286 11.9239C7.27897 11.5292 6.83721 11.2141 6.34546 10.9986C5.8537 10.783 5.32262 10.6718 4.78571 10.6718C4.24881 10.6718 3.71772 10.783 3.22597 10.9986C2.73421 11.2141 2.29246 11.5292 1.92857 11.9239C0.5 13.3525 0.5 19.0668 0.5 19.0668C0.5 19.0668 6.21429 19.0668 7.64286 17.6382Z"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.2429 1.32394C17.748 0.835368 17.0919 0.544053 16.3976 0.504604C15.7033 0.465155 15.0185 0.680282 14.4714 1.10966L6.21429 6.92394L5.2 10.6954C6.13088 10.8133 6.99315 11.247 7.64286 11.9239C8.31985 12.5737 8.7535 13.4359 8.87143 14.3668L12.6429 13.3525L18.4571 5.09537C18.8865 4.54834 19.1016 3.86349 19.0622 3.16919C19.0227 2.47489 18.7314 1.81882 18.2429 1.32394V1.32394Z"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M0.5 19.0668L5.14286 14.4239"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Click-to-sign PDF signer: renders the PDF once (stable — never reloaded), drops
// a marker on every "sig<n>" placeholder, and lets the tenant draw a fresh
// signature at each spot. Each drawn signature is kept in state and shown as an
// overlay image; the PDF is only actually stamped (pdf-sign-pad) once, at Save,
// which is enabled after every spot is signed. onSaved gets the signed base64.
const InlineSignPdf = ({ pdfBase64, placeholder, onSaved, onCancel }) => {
  const theme = useTheme();
  const { t } = useTranslation("choosingTenants");
  const padRef = useRef(null);
  const saveBtnRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [placeholders, setPlaceholders] = useState([]);
  // spot.id -> trimmed signature PNG data URL
  const [signed, setSigned] = useState({});
  const [activeSpot, setActiveSpot] = useState(null);
  const [padError, setPadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const renderWidth = useMemo(
    () =>
      Math.min(
        (typeof window !== "undefined" ? window.innerWidth : 800) - 48,
        720
      ),
    []
  );
  const padWidth = Math.min(renderWidth - 40, 420);

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, []);

  // Detect every placeholder once, using the app-supplied pattern.
  useEffect(() => {
    let cancelled = false;
    console.log("[InlineSignPdf] detecting with pattern:", placeholder, "hasPdf:", !!pdfBase64);
    findSignaturePlaceholders(pdfBase64, { pattern: placeholder })
      .then((found) => {
        console.log("[InlineSignPdf] placeholders found:", found.length, found);
        if (!cancelled) setPlaceholders(found);
      })
      .catch((e) => {
        console.error("[InlineSignPdf] detect failed:", e);
        if (!cancelled) setPlaceholders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pdfBase64, placeholder]);

  const signedCount = Object.keys(signed).length;
  const allSigned = placeholders.length > 0 && signedCount >= placeholders.length;
  const nextSpot = placeholders.find((p) => !signed[p.id]);

  const confirmSign = () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      setPadError(t("quote.needToSign"));
      return;
    }
    const png = trimmedPng(padRef.current.getCanvas());
    setSigned((prev) => ({ ...prev, [activeSpot.id]: png }));
    setActiveSpot(null);
    setPadError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const placements = placeholders
        .filter((p) => signed[p.id])
        .map((p) => ({ spot: p, pngDataUrl: signed[p.id] }));
      const stamped = await stampSignature(pdfBase64, placements);
      const finalPdf = await compressPdfIfNeeded(stamped);
      await onSaved?.(finalPdf);
    } catch (e) {
      setSaveError(t("quote.signError"));
    } finally {
      setSaving(false);
    }
  };

  // After each signature, jump to the next unsigned spot; once all are signed,
  // move focus to the Save button. (The PDF is stable, so markers stay mounted.)
  useEffect(() => {
    if (!placeholders.length || signedCount === 0) return;
    if (signedCount >= placeholders.length) {
      saveBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      saveBtnRef.current?.focus();
      return;
    }
    const el = nextSpot && document.getElementById(`sig-marker-${nextSpot.id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signed, placeholders]);

  const markerBase = {
    position: "absolute",
    transform: "translateY(-100%)",
    border: "none",
    borderRadius: 8,
    padding: "4px 10px",
    fontFamily: "Rubik",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    zIndex: 2,
  };

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center" }}>
        <Document
          file={pdfBase64}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={
            <div style={{ padding: 40 }}>
              <ClipLoader color={theme.color.primary} />
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, p) => {
            const spots = placeholders.filter((x) => x.pageIndex === p);
            return (
              <div
                key={p}
                style={{
                  position: "relative",
                  width: renderWidth,
                  margin: "0 auto 12px",
                }}
              >
                <Page
                  pageNumber={p + 1}
                  width={renderWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
                {spots.map((spot) => {
                  const s = renderWidth / spot.pageWidth;
                  const left = spot.x * s;
                  const baselineTop = (spot.pageHeight - spot.y) * s;
                  const png = signed[spot.id];
                  if (png) {
                    // show the drawn signature where it will be stamped
                    return (
                      <img
                        key={spot.id}
                        src={png}
                        alt="signature"
                        style={{
                          position: "absolute",
                          left,
                          top: baselineTop,
                          transform: "translateY(-100%)",
                          width: Math.max(spot.width, 90) * s,
                          height: "auto",
                          zIndex: 2,
                          pointerEvents: "none",
                        }}
                      />
                    );
                  }
                  const isNext = nextSpot && spot.id === nextSpot.id;
                  return (
                    <button
                      key={spot.id}
                      id={`sig-marker-${spot.id}`}
                      type="button"
                      onClick={() => setActiveSpot(spot)}
                      style={{
                        ...markerBase,
                        left,
                        top: baselineTop,
                        backgroundColor: theme.color.primary,
                        color: theme.color.white,
                        // highlight the next spot to sign
                        outline: isNext ? `3px solid ${theme.color.gray50}` : "none",
                        outlineOffset: 2,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <PenIcon color={theme.color.white} size={13} />
                      {t("quote.signHere")}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </Document>
      </div>

      {/* footer: progress + save/cancel */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "0 4px",
        }}
      >
        <span style={{ fontFamily: "Rubik", fontSize: 14, color: saveError ? "red" : theme.color.gray70 }}>
          {saveError || `${signedCount}/${placeholders.length} ${t("quote.signed")}`}
        </span>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: "transparent",
              color: theme.color.gray70,
              border: `1px solid ${theme.color.gray30}`,
              borderRadius: 12,
              padding: "8px 16px",
              cursor: "pointer",
              fontFamily: "Rubik",
            }}
          >
            {t("quote.cancel")}
          </button>
          <button
            ref={saveBtnRef}
            type="button"
            disabled={!allSigned || saving}
            onClick={handleSave}
            style={{
              background: allSigned ? theme.color.primary : theme.color.gray30,
              color: theme.color.white,
              border: "none",
              borderRadius: 12,
              padding: "8px 20px",
              cursor: allSigned && !saving ? "pointer" : "default",
              fontFamily: "Rubik",
              fontWeight: 500,
            }}
          >
            {saving ? <ClipLoader color={theme.color.white} size={16} /> : t("quote.save")}
          </button>
        </div>
      </div>

      {/* signature pad modal for the active spot */}
      {activeSpot && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(33,42,49,0.45)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              backgroundColor: theme.color.white,
              borderRadius: 14,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              maxWidth: "100%",
            }}
          >
            <span style={{ fontFamily: "Rubik", fontWeight: 600, fontSize: 17, color: theme.color.navy }}>
              {t("quote.signTitle")} — {activeSpot.label}
            </span>
            <SignatureCanvas
              ref={padRef}
              canvasProps={{
                width: padWidth,
                height: 200,
                style: {
                  border: `2px solid ${theme.color.gray30}`,
                  borderRadius: 8,
                  backgroundColor: theme.color.white,
                },
              }}
            />
            {padError && (
              <span style={{ color: "red", fontFamily: "Rubik", fontSize: 13 }}>
                {padError}
              </span>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setActiveSpot(null);
                  setPadError(null);
                }}
                style={{
                  background: "transparent",
                  color: theme.color.gray70,
                  border: `1px solid ${theme.color.gray30}`,
                  borderRadius: 12,
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontFamily: "Rubik",
                }}
              >
                {t("quote.cancel")}
              </button>
              <button
                type="button"
                onClick={() => padRef.current?.clear()}
                style={{
                  background: "transparent",
                  color: theme.color.primary,
                  border: "none",
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontFamily: "Rubik",
                }}
              >
                {t("quote.clear")}
              </button>
              <button
                type="button"
                onClick={confirmSign}
                style={{
                  background: theme.color.primary,
                  color: theme.color.white,
                  border: "none",
                  borderRadius: 12,
                  padding: "8px 20px",
                  cursor: "pointer",
                  fontFamily: "Rubik",
                  fontWeight: 500,
                }}
              >
                {t("quote.sign")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineSignPdf;
